import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { InstanceApiFactory } from '../api/instanceApi';
import type {
  FilterState,
  PaginationMeta,
  SortState,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import type { InstanceRow } from '../instanceTypes';
import { DEFAULT_PAGE, DEFAULT_TABLE_PAGE_SIZE } from '../../../hooks/usePaginationState';

interface UseGetInstancesResult {
  data: InstanceRow[];
  loading: boolean;
  error: ApiError | Error | null;
  meta: PaginationMeta | null;
  refetch: () => void;
}

export function useGetInstances(
  filters?: FilterState<InstanceRow>,
  columns?: TableColumn<InstanceRow>[],
  sortState?: SortState<InstanceRow> | null,
  page: number = DEFAULT_PAGE,
  limit: number = DEFAULT_TABLE_PAGE_SIZE,
): UseGetInstancesResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => InstanceApiFactory(client), [client]);

  const [data, setData] = useState<InstanceRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [revision, setRevision] = useState(0);

  const refetch = useCallback(() => setRevision(r => r + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();
    setError(null);

    api
      .getAll(filters, columns, sortState, page, limit, { signal: ctrl.signal })
      .then(response => {
        setData(response.data);
        setMeta(response.meta);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        // Never leave the previous page's rows displayed under the error banner.
        setData([]);
        setMeta(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, JSON.stringify(filters), JSON.stringify(sortState), page, limit, revision]);

  return { data, loading, error, meta, refetch };
}
